import React, { useEffect, useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { ColorRing } from "react-loader-spinner";
import Pagination from "@mui/material/Pagination";

import API_BASE_URL from "../../../../Constants";
import RowsPerPageSelector from "../../../../components/Tables/Pagination/RowsPerPageSelector";

// ★ Import your ListHeader
import ListHeader from "../../../../components/ListHeader/NoCardView";
// or wherever your "ListHeader" is located

import ViewAccountPricingModal from "./ViewAccountPricingModal";
import CreateAccountPricingModal from "./CreateAccountPricingModal";

export default function AccountPricingList() {
  // ---------------------------
  // Local State & Logic
  // ---------------------------
  const [accountPricings, setAccountPricings] = useState([]);
  const [filteredAccountPricings, setFilteredAccountPricings] = useState([]);

  // Searching
  const [searchQuery, setSearchQuery] = useState("");
  const [searchCriteria, setSearchCriteria] = useState("none");

  // Table/Card
  const [viewMode, setViewMode] = useState("table");

  // Modals
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPricingId, setSelectedPricingId] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(null);

  const [isCreatePricingOpen, setIsCreatePricingOpen] = useState(false);

  // Account level
  const [levelFilter, setLevelFilter] = useState("BUILDING");

  // Loading spinner
  const [isLoading, setIsLoading] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Table columns
  const tableFields = [
    { key: "actions", label: "Actions" },
    { key: "agencyName", label: "Agency" },
    { key: "testLabel", label: "Test ID" },
    { key: "level", label: "Level" },
    { key: "account", label: "Account" },
    { key: "dateFrom", label: "Date from" },
    { key: "dateTo", label: "Date to" },
    { key: "amount", label: "Amount" },
  ];

  // 1) Fetch data
  const fetchIntersectionData = async (level) => {
    try {
      setIsLoading(true);
      let url = "";
      let displayLevel = "";

      switch (level) {
        case "BUILDING":
          url = `${API_BASE_URL}/api/inspections/account-pricing-building`;
          displayLevel = "Building";
          break;
        case "CUSTOMER":
          url = `${API_BASE_URL}/api/inspections/account-pricing-customer`;
          displayLevel = "Customer";
          break;
        case "MGMTCO":
          url = `${API_BASE_URL}/api/inspections/account-pricing-management`;
          displayLevel = "Management";
          break;
        case "SERVICECO":
          url = `${API_BASE_URL}/api/inspections/account-pricing-serviceco`;
          displayLevel = "Service Co";
          break;
        default:
          url = `${API_BASE_URL}/api/inspections/account-pricing-building`;
          displayLevel = "Building";
      }

      const resp = await fetch(url, { credentials: "include" });
      if (!resp.ok) {
        throw new Error(`Fetch error => ${resp.status}`);
      }
      const data = await resp.json();
      const arr = Array.isArray(data) ? data : [];

      const finalData = arr.map((row) => ({
        accountPricingId: row.accountPricingId,
        agencyName: row.agencyName,
        testLabel: row.testLabel,
        dateFrom: row.dateFrom,
        dateTo: row.dateTo,
        amount: row.amount,
        level: displayLevel,
        account:
          row.buildingReferenceName ||
          row.companyName ||
          row.customerName ||
          row.account ||
          "(N/A)",
      }));

      setAccountPricings(finalData);
      setFilteredAccountPricings(finalData);
      setCurrentPage(1);
    } catch (err) {
      console.error("Error fetching data:", err);
      setAccountPricings([]);
      setFilteredAccountPricings([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchIntersectionData(levelFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [levelFilter]);

  // 2) Searching
  const handleSearchChange = (e) => setSearchQuery(e.target.value);

  const handleSearch = () => {
    let data = [...accountPricings];
    if (searchCriteria !== "none" && searchQuery) {
      const lowerVal = searchQuery.toLowerCase();
      data = data.filter((ap) => {
        const val = ap[searchCriteria]?.toString().toLowerCase();
        return val && val.includes(lowerVal);
      });
    }
    setFilteredAccountPricings(data);
    setCurrentPage(1);
  };

  // 3) Excel
  const handleExcelClick = () => {
    if (!filteredAccountPricings.length) {
      alert("No data to export!");
      return;
    }
    const sheet = XLSX.utils.json_to_sheet(filteredAccountPricings);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, sheet, "Sheet1");
    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([wbout], { type: "application/octet-stream" }),
      "AccountPricing.xlsx"
    );
  };

  const handleActionsClick = () => alert("Actions clicked!");

  // 4) Add => open create modal
  const handleOpenCreateModal = () => setIsCreatePricingOpen(true);

  // 5) Table/Card
  const handleViewModeChange = (val) => setViewMode(val);

  // 6) Pagination
  const totalItems = filteredAccountPricings.length;
  const totalPages = Math.ceil(totalItems / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const displayedPricings = filteredAccountPricings.slice(startIndex, endIndex);

  const handlePageChange = (event, page) => {
    setCurrentPage(page);
  };
  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  // 7) “View”
  const handleViewClick = (idx) => {
    const realIndex = idx + startIndex;
    setSelectedIndex(realIndex);
    setSelectedPricingId(filteredAccountPricings[realIndex].accountPricingId);
    setIsModalOpen(true);
  };
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedIndex(null);
    setSelectedPricingId(null);
  };
  const handleNext = () => {
    if (selectedIndex < filteredAccountPricings.length - 1) {
      const newIndex = selectedIndex + 1;
      setSelectedIndex(newIndex);
      setSelectedPricingId(filteredAccountPricings[newIndex].accountPricingId);
    }
  };
  const handlePrev = () => {
    if (selectedIndex > 0) {
      const newIndex = selectedIndex - 1;
      setSelectedIndex(newIndex);
      setSelectedPricingId(filteredAccountPricings[newIndex].accountPricingId);
    }
  };

  const refreshList = () => {
    fetchIntersectionData(levelFilter);
  };

  return (
    <div className="bg-white w-full min-h-screen pt-4">
      {/* 
        Replacing the old top bar with ListHeader.
        We pass searchQuery, handleSearchChange, handleSearch, 
        plus a "viewMode" for table/card if you want.
      */}
      <ListHeader
        title="Account Pricing List"
        searchQuery={searchQuery}
        handleSearchChange={handleSearchChange}
        handleSearch={handleSearch}
        viewMode={viewMode}
        handleViewModeChange={handleViewModeChange}
        handleOpenCreateModal={handleOpenCreateModal}
      >
        {/* 
          All custom filters & buttons go here as children 
          so they line up in one row with the search bar. 
        */}
        <div className="flex items-center gap-2">
          {/* Filter Criteria */}
          <label className="font-bold text-gray-700">Filter:</label>
          <select
            value={searchCriteria}
            onChange={(e) => setSearchCriteria(e.target.value)}
            className="px-3 py-2 border-2 border-sss-blue-sel
                       rounded-lg font-bold text-gray-700 
                       hover:bg-gray-50 transition"
          >
            <option value="none">No Filter</option>
            <option value="agencyName">Agency</option>
            <option value="testLabel">Test ID</option>
            <option value="level">Level</option>
            <option value="account">Account</option>
          </select>

          {/* Account Level Filter */}
          <label className="font-bold text-gray-700">Level:</label>
          <select
            value={levelFilter}
            onChange={(e) => setLevelFilter(e.target.value)}
            className="px-3 py-2 border-2 border-sss-blue-sel
                       rounded-lg font-bold text-gray-700 
                       hover:bg-gray-50 transition"
          >
            <option value="BUILDING">Building</option>
            <option value="CUSTOMER">Customer</option>
            <option value="MGMTCO">Management</option>
            <option value="SERVICECO">Service Co</option>
          </select>

          {/* Excel Button */}
          <button
            onClick={handleExcelClick}
            className="px-4 py-2 border-2 border-sss-blue-sel 
                       rounded-lg text-sss-blue-sel font-bold 
                       hover:bg-sss-blue-sel hover:text-white transition"
          >
            Excel
          </button>

          {/* “Actions” Button */}
          <button
            onClick={handleActionsClick}
            className="px-4 py-2 border-2 border-sss-blue-sel 
                       rounded-lg text-sss-blue-sel font-bold 
                       hover:bg-sss-blue-sel hover:text-white transition"
          >
            Actions
          </button>
        </div>
      </ListHeader>

      {/* MAIN CONTENT */}
      {isLoading && (
        <div className="flex justify-center items-center py-8">
          <ColorRing
            visible
            height={200}
            width={200}
            ariaLabel="color-ring-loading"
            colors={["#033B5C", "#033B5C", "#033B5C", "#033B5C", "#033B5C"]}
          />
        </div>
      )}

      {!isLoading && (
        <>
          {viewMode === "table" && (
            <div className="mx-4 overflow-x-auto">
              <table className="min-w-full bg-white rounded-lg shadow-lg overflow-hidden border-2 border-gray-200 mt-4">
                <thead className="bg-gray-100">
                  <tr>
                    {tableFields.map((field) => (
                      <th
                        key={field.key}
                        className="px-4 py-2 text-left font-bold text-sss-blue-sel text-sm whitespace-nowrap"
                      >
                        {field.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="text-gray-700 text-sm">
                  {displayedPricings.length > 0 ? (
                    displayedPricings.map((ap, idx) => (
                      <tr
                        key={idx}
                        className="border-t hover:bg-gray-50 transition"
                      >
                        {tableFields.map((col) => {
                          if (col.key === "actions") {
                            return (
                              <td key={col.key} className="px-4 py-2">
                                <button
                                  className="text-blue-600 underline"
                                  onClick={() => handleViewClick(idx)}
                                >
                                  View
                                </button>
                              </td>
                            );
                          }
                          return (
                            <td
                              key={col.key}
                              className="px-4 py-2 whitespace-nowrap"
                            >
                              {ap[col.key] ?? "N/A"}
                            </td>
                          );
                        })}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={tableFields.length}
                        className="text-center py-4"
                      >
                        No account pricing found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {viewMode === "card" && (
            <div className="m-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {displayedPricings.length > 0 ? (
                displayedPricings.map((ap, idx) => (
                  <div
                    key={idx}
                    className="bg-white p-4 rounded-lg shadow-lg border-2 border-gray-200 flex flex-col"
                  >
                    <h3 className="text-md font-bold mb-2">
                      {ap.agencyName ?? "Agency"} / {ap.testLabel ?? "Test"}
                    </h3>
                    <p>Level: {ap.level}</p>
                    <p>Account: {ap.account}</p>
                    <p>Date from: {ap.dateFrom}</p>
                    <p>Date to: {ap.dateTo}</p>
                    <p>Amount: {ap.amount}</p>

                    <button
                      className="mt-2 text-blue-600 underline self-start"
                      onClick={() => handleViewClick(idx)}
                    >
                      View
                    </button>
                  </div>
                ))
              ) : (
                <p className="col-span-full text-center py-4">
                  No account pricing found.
                </p>
              )}
            </div>
          )}

          {/* Pagination */}
          <div className="flex justify-between items-center p-4 border-t border-gray-300 bg-white">
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
              shape="rounded"
            />
            <RowsPerPageSelector
              rowsPerPage={rowsPerPage}
              setRowsPerPage={handleRowsPerPageChange}
            />
          </div>
        </>
      )}

      {/* View Modal */}
      {isModalOpen && selectedPricingId && (
        <ViewAccountPricingModal
          open={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          pricingId={selectedPricingId}
          refreshList={refreshList}
          setNext={handleNext}
          setPrev={handlePrev}
        />
      )}

      {/* Create Modal */}
      {isCreatePricingOpen && (
        <CreateAccountPricingModal
          open={isCreatePricingOpen}
          onClose={() => setIsCreatePricingOpen(false)}
          refreshList={refreshList}
          currentLevel={levelFilter}
        />
      )}
    </div>
  );
}
